import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { ShoppingCart, Plus, Edit2, Trash2, ExternalLink, DollarSign, Package, Search, Filter, ClipboardCheck } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const QuickBuy = () => {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseItem, setPurchaseItem] = useState(null);
  const [purchaseData, setPurchaseData] = useState({ quantity: 1, costPerUnit: '' });
  const [recordingPurchase, setRecordingPurchase] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    vendor: '',
    productUrl: '',
    price: '',
    category: '',
    notes: '',
    imageUrl: '',
  });

  useEffect(() => {
    loadItems();
    loadCategories();
  }, []);

  useEffect(() => {
    filterItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchTerm, selectedCategory]);

  const loadItems = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/quick-buy`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(response.data);
    } catch (error) {
      console.error('Load items error:', error);
      toast.error('Failed to load items');
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/quick-buy/categories`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Load categories error:', error);
    }
  };

  const filterItems = () => {
    let filtered = items;

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.vendor.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(item => item.category === selectedCategory);
    }

    setFilteredItems(filtered);
  };

  const handleOpenModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        name: item.name,
        description: item.description || '',
        vendor: item.vendor,
        productUrl: item.product_url,
        price: item.price || '',
        category: item.category || '',
        notes: item.notes || '',
        imageUrl: item.image_url || '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        name: '',
        description: '',
        vendor: '',
        productUrl: '',
        price: '',
        category: '',
        notes: '',
        imageUrl: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = sessionStorage.getItem('token');
      const payload = {
        name: formData.name,
        description: formData.description,
        vendor: formData.vendor,
        productUrl: formData.productUrl,
        price: formData.price ? parseFloat(formData.price) : null,
        category: formData.category,
        notes: formData.notes,
        imageUrl: formData.imageUrl,
      };

      if (editingItem) {
        await axios.put(`${API_URL}/quick-buy/${editingItem.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Item updated successfully!');
      } else {
        await axios.post(`${API_URL}/quick-buy`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Item added successfully!');
      }

      handleCloseModal();
      loadItems();
      loadCategories();
    } catch (error) {
      console.error('Save item error:', error);
      toast.error(error.response?.data?.error || 'Failed to save item');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }

    try {
      const token = sessionStorage.getItem('token');
      await axios.delete(`${API_URL}/quick-buy/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Item deleted successfully!');
      loadItems();
      loadCategories();
    } catch (error) {
      console.error('Delete item error:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleToggleActive = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/quick-buy/${id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      loadItems();
    } catch (error) {
      console.error('Toggle item error:', error);
      toast.error('Failed to toggle item status');
    }
  };

  const handleBuyNow = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOpenPurchaseModal = (item) => {
    setPurchaseItem(item);
    setPurchaseData({ quantity: 1, costPerUnit: item.price || '' });
    setShowPurchaseModal(true);
  };

  const handleRecordPurchase = async (e) => {
    e.preventDefault();
    if (!purchaseItem) return;

    setRecordingPurchase(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/quick-buy/${purchaseItem.id}/record-purchase`,
        {
          quantity: parseInt(purchaseData.quantity),
          costPerUnit: purchaseData.costPerUnit ? parseFloat(purchaseData.costPerUnit) : null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Purchase recorded! ${response.data.quantityAdded} units added to inventory.`);
      setShowPurchaseModal(false);
      setPurchaseItem(null);
    } catch (error) {
      console.error('Record purchase error:', error);
      toast.error(error.response?.data?.error || 'Failed to record purchase');
    } finally {
      setRecordingPurchase(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ShoppingCart size={32} className="text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold">Quick Buy</h1>
            <p className="text-gray-600">Frequently purchased supplies and equipment</p>
          </div>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Item</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search items..."
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Items Grid */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="card text-center py-12">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600">No items found. Add your first quick buy item!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredItems.map((item) => (
            <div
              key={item.id}
              className={`card hover:shadow-lg transition-shadow ${!item.is_active ? 'opacity-50' : ''}`}
            >
              {item.image_url && (
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="w-full h-48 object-cover rounded-t-lg mb-4"
                />
              )}

              <div className="flex items-start justify-between mb-2">
                <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                {item.price && (
                  <span className="flex items-center text-lg font-semibold text-green-600">
                    <DollarSign size={16} />
                    {parseFloat(item.price).toFixed(2)}
                  </span>
                )}
              </div>

              {item.category && (
                <span className="inline-block px-2 py-1 bg-primary-100 text-primary-800 text-xs rounded-full mb-2">
                  {item.category}
                </span>
              )}

              {item.description && (
                <p className="text-sm text-gray-600 mb-3">{item.description}</p>
              )}

              <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                <Package size={16} />
                <span className="font-medium">{item.vendor}</span>
              </div>

              {item.notes && (
                <p className="text-xs text-gray-500 italic mb-3">{item.notes}</p>
              )}

              <div className="flex space-x-2">
                <button
                  onClick={() => handleBuyNow(item.product_url)}
                  className="flex-1 btn btn-primary flex items-center justify-center space-x-2"
                >
                  <ExternalLink size={16} />
                  <span>Buy Now</span>
                </button>
                <button
                  onClick={() => handleOpenPurchaseModal(item)}
                  className="btn bg-green-600 text-white hover:bg-green-700"
                  title="Record Purchase to Inventory"
                >
                  <ClipboardCheck size={16} />
                </button>
                <button
                  onClick={() => handleOpenModal(item)}
                  className="btn bg-gray-600 text-white hover:bg-gray-700"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(item.id)}
                  className="btn bg-red-600 text-white hover:bg-red-700"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="mt-3 pt-3 border-t">
                <label className="flex items-center space-x-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.is_active}
                    onChange={() => handleToggleActive(item.id)}
                    className="w-4 h-4 text-primary-600 rounded"
                  />
                  <span className="text-gray-600">Active</span>
                </label>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Record Purchase Modal */}
      {showPurchaseModal && purchaseItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b">
              <h2 className="text-xl font-bold flex items-center space-x-2">
                <ClipboardCheck size={24} className="text-green-600" />
                <span>Record Purchase</span>
              </h2>
              <p className="text-sm text-gray-600 mt-1">Add purchased items to inventory</p>
            </div>

            <form onSubmit={handleRecordPurchase} className="p-6 space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold text-gray-900">{purchaseItem.name}</p>
                <p className="text-sm text-gray-600">{purchaseItem.vendor}</p>
              </div>

              <div>
                <label className="label">Quantity Purchased *</label>
                <input
                  type="number"
                  min="1"
                  value={purchaseData.quantity}
                  onChange={(e) => setPurchaseData(prev => ({ ...prev, quantity: e.target.value }))}
                  required
                  className="input"
                  placeholder="e.g., 10"
                />
              </div>

              <div>
                <label className="label">Cost Per Unit ($)</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchaseData.costPerUnit}
                  onChange={(e) => setPurchaseData(prev => ({ ...prev, costPerUnit: e.target.value }))}
                  className="input"
                  placeholder={purchaseItem.price ? `Default: $${parseFloat(purchaseItem.price).toFixed(2)}` : 'e.g., 9.99'}
                />
                <p className="text-xs text-gray-500 mt-1">Leave blank to use the item's listed price</p>
              </div>

              {purchaseData.quantity && purchaseData.costPerUnit && (
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg">
                  <p className="text-sm text-green-800">
                    Total cost: <span className="font-bold">${(parseFloat(purchaseData.quantity) * parseFloat(purchaseData.costPerUnit)).toFixed(2)}</span>
                  </p>
                </div>
              )}

              <div className="flex space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowPurchaseModal(false); setPurchaseItem(null); }}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={recordingPurchase}
                  className="flex-1 btn btn-primary flex items-center justify-center space-x-2"
                >
                  <Package size={16} />
                  <span>{recordingPurchase ? 'Recording...' : 'Add to Inventory'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b">
              <h2 className="text-2xl font-bold">
                {editingItem ? 'Edit Item' : 'Add New Item'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Item Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input"
                    placeholder="e.g., Dog Shampoo"
                  />
                </div>

                <div>
                  <label className="label">Vendor *</label>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleChange}
                    required
                    className="input"
                    placeholder="e.g., Amazon, Chewy"
                  />
                </div>
              </div>

              <div>
                <label className="label">Product URL *</label>
                <input
                  type="url"
                  name="productUrl"
                  value={formData.productUrl}
                  onChange={handleChange}
                  required
                  className="input"
                  placeholder="https://..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="input"
                    placeholder="29.99"
                  />
                </div>

                <div>
                  <label className="label">Category</label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className="input"
                    placeholder="e.g., Shampoo & Conditioner"
                    list="categories"
                  />
                  <datalist id="categories">
                    {categories.map(cat => (
                      <option key={cat} value={cat} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="label">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="input resize-none"
                  placeholder="Brief description of the item..."
                />
              </div>

              <div>
                <label className="label">Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={2}
                  className="input resize-none"
                  placeholder="Reorder frequency, quantity notes, etc..."
                />
              </div>

              <div>
                <label className="label">Image URL (optional)</label>
                <input
                  type="url"
                  name="imageUrl"
                  value={formData.imageUrl}
                  onChange={handleChange}
                  className="input"
                  placeholder="https://..."
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="flex-1 btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 btn btn-primary"
                >
                  {editingItem ? 'Update Item' : 'Add Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuickBuy;
